import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useMfaStatus } from '@/hooks/useMfaStatus';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { Shield, ShieldCheck, ShieldOff, Loader2, Copy, Check } from 'lucide-react';

type SetupStep = 'idle' | 'enrolling' | 'verifying';

export default function MfaSetupCard() {
  const { hasRole } = useAuth();
  const { verifiedFactor, isEnrolled, isVerifiedThisSession, isLoading, refresh } = useMfaStatus();
  const { toast } = useToast();
  const isSuperAdmin = hasRole('super_admin');

  // Enrolment flow state
  const [step, setStep] = useState<SetupStep>('idle');
  const [enrolledFactorId, setEnrolledFactorId] = useState('');
  const [qrUri, setQrUri] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);

  // Unenrol confirm dialog
  const [unenrolOpen, setUnenrolOpen] = useState(false);

  const startEnrolment = async () => {
    setSubmitting(true);
    try {
      const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp' });
      if (error || !data) throw new Error(error?.message ?? 'Enrolment failed');
      setEnrolledFactorId(data.id);
      setQrUri(data.totp.qr_code);
      setSecret(data.totp.secret);
      setCode('');
      setStep('enrolling');
    } catch (err: unknown) {
      toast({ title: 'Setup failed', description: err instanceof Error ? err.message : 'Could not start MFA setup.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const cancelEnrolment = async () => {
    if (enrolledFactorId) {
      await supabase.auth.mfa.unenroll({ factorId: enrolledFactorId }).catch(() => null);
    }
    setStep('idle');
    setEnrolledFactorId('');
    setQrUri('');
    setSecret('');
    setCode('');
  };

  const verifyEnrolment = async () => {
    if (code.length !== 6) return;
    setSubmitting(true);
    try {
      const { data: challenge, error: cErr } = await supabase.auth.mfa.challenge({ factorId: enrolledFactorId });
      if (cErr || !challenge) throw new Error(cErr?.message ?? 'Challenge failed');

      const { error: vErr } = await supabase.auth.mfa.verify({
        factorId: enrolledFactorId,
        challengeId: challenge.id,
        code,
      });
      if (vErr) throw new Error(vErr.message);

      toast({ title: 'MFA enabled', description: 'Two-factor authentication is now active on your account.' });
      setStep('idle');
      setQrUri('');
      setSecret('');
      setCode('');
      await refresh();
    } catch (err: unknown) {
      toast({ title: 'Incorrect code', description: err instanceof Error ? err.message : 'Please try again.', variant: 'destructive' });
      setCode('');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnenrol = async () => {
    if (!verifiedFactor) return;
    setSubmitting(true);
    try {
      const { error } = await supabase.auth.mfa.unenroll({ factorId: verifiedFactor.id });
      if (error) throw new Error(error.message);
      toast({ title: 'MFA disabled', description: 'Two-factor authentication has been removed from your account.' });
      await refresh();
    } catch (err: unknown) {
      toast({ title: 'Could not disable MFA', description: err instanceof Error ? err.message : 'Please try again.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
      setUnenrolOpen(false);
    }
  };

  const copySecret = async () => {
    await navigator.clipboard.writeText(secret);
    setSecretCopied(true);
    setTimeout(() => setSecretCopied(false), 2000);
  };

  if (isLoading) {
    return (
      <Card className="shadow-soft">
        <CardContent className="flex items-center gap-2 py-6 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Loading security settings…</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="shadow-soft">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Two-factor authentication</CardTitle>
            {isEnrolled
              ? <Badge variant="secondary" className="ml-auto text-xs bg-green-100 text-green-700 border-green-200">Enabled</Badge>
              : <Badge variant="outline" className="ml-auto text-xs text-muted-foreground">Not enabled</Badge>}
          </div>
          <CardDescription>
            Add an extra layer of security by requiring a code from an authenticator app at sign-in.
            {isSuperAdmin && !isEnrolled && (
              <span className="block mt-1 text-amber-600 font-medium">
                Required for super admin accounts — set up MFA now.
              </span>
            )}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* ── Idle: not enrolled ── */}
          {step === 'idle' && !isEnrolled && (
            <Button onClick={startEnrolment} disabled={submitting}>
              {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Starting setup…</> : 'Set up authenticator app'}
            </Button>
          )}

          {/* ── Idle: enrolled ── */}
          {step === 'idle' && isEnrolled && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-green-700">
                <ShieldCheck className="h-4 w-4" />
                <span>MFA is active. You will be asked for a code at each sign-in.</span>
              </div>
              {!isSuperAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setUnenrolOpen(true)}
                  disabled={submitting || !isVerifiedThisSession}
                >
                  <ShieldOff className="h-4 w-4 mr-2" />
                  Disable MFA
                </Button>
              )}
              {!isVerifiedThisSession && !isSuperAdmin && (
                <p className="text-xs text-muted-foreground">You must verify your MFA code this session before you can disable it.</p>
              )}
              {isSuperAdmin && (
                <p className="text-xs text-muted-foreground">MFA cannot be disabled on super admin accounts. Contact another super admin if you need to replace your authenticator.</p>
              )}
            </div>
          )}

          {/* ── Enrolling: show QR ── */}
          {step === 'enrolling' && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <p className="text-sm font-medium">Scan this QR code</p>
                <p className="text-xs text-muted-foreground">
                  Open your authenticator app (Google Authenticator, Authy, 1Password, etc.) and scan the code below.
                </p>
              </div>

              {qrUri && (
                <div className="flex justify-center">
                  <div className="border rounded-lg p-3 bg-white inline-block">
                    <img src={qrUri} alt="MFA QR code" className="h-40 w-40" />
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <p className="text-xs text-muted-foreground">
                  Can't scan? Enter this code manually in your authenticator app:
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 rounded bg-muted px-3 py-1.5 text-xs font-mono break-all">{secret}</code>
                  <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={copySecret}>
                    {secretCopied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              </div>

              <div className="space-y-1.5 pt-1">
                <Label htmlFor="mfa-enrol-code">Enter the 6-digit code to confirm setup</Label>
                <Input
                  id="mfa-enrol-code"
                  value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  onKeyDown={e => { if (e.key === 'Enter') verifyEnrolment(); }}
                />
              </div>

              <div className="flex gap-2">
                <Button variant="outline" onClick={cancelEnrolment} disabled={submitting}>
                  Cancel
                </Button>
                <Button onClick={verifyEnrolment} disabled={code.length !== 6 || submitting}>
                  {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Activating…</> : 'Activate MFA'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={unenrolOpen} onOpenChange={setUnenrolOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable two-factor authentication?</AlertDialogTitle>
            <AlertDialogDescription>
              Your account will only be protected by your password. You can re-enable MFA at any time from Settings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep MFA enabled</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleUnenrol}
              disabled={submitting}
            >
              {submitting ? 'Disabling…' : 'Disable MFA'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
